import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Collapse, Box } from '@mui/material';

const STATUS_MESSAGES = {
  'go': '✅ No Nearby Hazards',
  'advisory': 'ℹ️ Advisory: Hazards Nearby',
  'caution': '⚠️ Caution: Risk of Obstacle or Airspace',
  'no-go': '❌ Do Not Fly: Dangerous Obstacle or Restricted Area',
  'unknown': 'ℹ️ Unknown'
};

const NearbyHazardsCard = ({ lat, lon, refreshTrigger, onUpdate }) => {
  const [severity, setSeverity] = useState('unknown');
  const [summaryText, setSummaryText] = useState('Loading hazard data...');
  const [detailText, setDetailText] = useState('');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchHazards = async () => {
      if (!lat || !lon) {
        setSeverity('advisory');
        setSummaryText(STATUS_MESSAGES['advisory']);
        setDetailText('No location set to check for hazards.');
        onUpdate && onUpdate('advisory', ['No location']);
        return;
      }

      try {
        const url = `https://flightiq.app/API/check-airspacev2.php?lat=${lat}&lon=${lon}&radius=5000&type=all`;
        const res = await fetch(url);
        const data = await res.json();

        const findings = [];
        let sev = 'go';

        // Check obstacles
        const obstacles = data.obs?.features || [];
        for (const feat of obstacles) {
          const elev = parseInt(feat.properties?.elevation_agl ?? feat.properties?.elevation ?? 0);
          if (elev <= 400) {
            sev = 'advisory';
            findings.push(`Obstacle: ${elev} ft AGL`);
          } else if (elev <= 700 && sev !== 'no-go') {
            sev = 'advisory';
            findings.push(`Obstacle: ${elev} ft AGL`);
          }
        }

        // Check restricted areas
        const raa = data.raa?.features || [];
        if (raa.length > 0 && sev !== 'no-go') {
          sev = 'no-go';
          findings.push(`${raa.length} restricted area(s)`);
        }



// Check airports and show closest ones in miles
const airports = (data.apt?.features || []).sort((a, b) => {
  const dA = a.properties?.distance_m ?? Infinity;
  const dB = b.properties?.distance_m ?? Infinity;
  return dA - dB;
});

if (airports.length > 0 && sev === 'go') {
  const closest = airports[0];
  const distM   = closest?.properties?.distance_m ?? Infinity;
  const distMi  = distM / 1609.34;

  if (distMi < 0.25) {
    sev = 'caution';
    findings.push(`⚠️ Closest airport < 0.25 mi – ${closest.properties?.name || 'Unnamed Airport'}`);
  } else if (distMi < 3.0) {
    sev = 'advisory';
    findings.push(`ℹ️ Closest airport < 3 mi – ${closest.properties?.name || 'Unnamed Airport'}`);
  }

  // List all nearby airports
  airports.forEach((apt, i) => {
    const dist = apt.properties?.distance_m;
    const name = apt.properties?.name || `Airport ${i + 1}`;
    if (dist) {
      const miles = (dist / 1609.34).toFixed(1);
      findings.push(`${name} – ${miles} mi away`);
    } else {
      findings.push(`${name}`);
    }
  });
}


        // Advisory layers
        const hot = data.hot?.features?.length || 0;
        const rca = data.rca?.features?.length || 0;
        const hgl = data.hgl?.features?.length || 0;
        if ((hot + rca + hgl) > 0 && sev === 'go') {
          sev = 'advisory';
          findings.push(`${hot} hot zones, ${rca} RC fields, ${hgl} hang glider sites`);
        }

        const summary = STATUS_MESSAGES[sev] || STATUS_MESSAGES['unknown'];
        const details = findings.map((f, i) => `<div>${i + 1}. ${f}</div>`).join('<br/>');

        setSeverity(sev);
        setSummaryText(summary);
        setDetailText(details);
        onUpdate && onUpdate(sev, findings);

      } catch (err) {
        console.error('NearbyHazards fetch error:', err);
        setSeverity('advisory');
        setSummaryText(STATUS_MESSAGES['advisory']);
        setDetailText('Hazard data unavailable.');
        onUpdate && onUpdate('advisory', ['Hazard data unavailable']);
      }
    };

    fetchHazards();
  }, [lat, lon, refreshTrigger]);

  const borderColor = severity === 'no-go' ? 'red'
                    : severity === 'caution' ? 'orange'
                    : severity === 'advisory' ? 'yellow'
                    : severity === 'go' ? 'green'
                    : 'gray';

  return (
  
    <Card 
      onClick={() => setExpanded(!expanded)}
      sx={{ cursor: 'pointer', borderLeft: `6px solid ${borderColor}`, mb: 1 }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Nearby Hazards</Typography>
          <Typography variant="body1" sx={{ opacity: 0.7 }}>🚧</Typography>
        </Box>
        <Typography variant="subtitle1" sx={{ mt: 1 }} dangerouslySetInnerHTML={{ __html: summaryText }} />
        <Collapse in={expanded}>
          <Typography variant="body2" sx={{ mt: 1 }} dangerouslySetInnerHTML={{ __html: detailText }} />
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default NearbyHazardsCard;
